import { z } from "zod";
import {
	tipoReduccionSchema,
	periodicidadSchema,
	requiredDateString,
	requiredPositiveNumber,
	optionalString,
	optionalDateString,
	timestampFieldsSchema,
	createPaginatedSchema,
	listParamsSchema,
} from "@/schemas/common";

// =============================================================================
// AMORTIZATION SCHEMAS
// =============================================================================

/** Base object shape — used for extending (response schema) */
const amortizationBaseSchema = z.object({
	tipo_reduccion: tipoReduccionSchema,
	monto: requiredPositiveNumber,
	periodicidad: periodicidadSchema.default("unica"),
	fecha: requiredDateString,
	fecha_fin: optionalDateString,
	cuota_mes_pagada: z.boolean().default(false),
	descripcion: optionalString,
	metadatos: z.record(z.unknown()).optional(),
});

/** Full schema with cross-field validation — use for forms and API requests */
export const amortizationSchema = amortizationBaseSchema.superRefine(
	(data, ctx) => {
		const isRecurring = data.periodicidad !== "unica";

		// fecha_fin required when recurring
		if (isRecurring && !data.fecha_fin) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "required_date",
				path: ["fecha_fin"],
			});
		}

		// fecha_fin must be after fecha when recurring
		if (isRecurring && data.fecha_fin && data.fecha_fin <= data.fecha) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "end_date_after_start",
				path: ["fecha_fin"],
			});
		}
	},
);

export const amortizationResponseSchema = amortizationBaseSchema.extend({
	id: z.number(),
	id_hipoteca: z.number(),
	...timestampFieldsSchema.shape,
});

export const amortizationPaginatedSchema = createPaginatedSchema(
	amortizationResponseSchema,
);

export const amortizationListParamsSchema = listParamsSchema.extend({
	tipo_reduccion: tipoReduccionSchema.optional(),
});

// Metadatos shape for pago_total amortizations
export const pagoTotalMetadatosSchema = z.object({
	tipo_transicion: z.enum(["compra_deuda", "refinanciada"]).optional(),
	costos_evitados_origen: z
		.object({
			total: z.number(),
			portes: z.number(),
			interes: z.number(),
			seguro_bien: z.number(),
			seguro_desgravamen: z.number(),
		})
		.optional(),
	liquidacion: z
		.object({
			total_pago: z.string(),
			dias_devengados: z.number(),
			fecha_operacion: z.string(),
			interes_devengado: z.string(),
			portes_devengados: z.string(),
			seguro_bien_devengado: z.string(),
			saldo_capital_pendiente: z.string(),
			seguro_desgravamen_devengado: z.string(),
		})
		.optional(),
});

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type AmortizationListParams = z.infer<typeof amortizationListParamsSchema>;
export type PagoTotalMetadatos = z.infer<typeof pagoTotalMetadatosSchema>;
/** Input type for forms (before Zod transforms/defaults) */
export type AmortizationFormInput = z.input<typeof amortizationSchema>;
/** Output type after Zod parsing (defaults applied) */
export type AmortizationFormData = z.infer<typeof amortizationSchema>;
export type AmortizationResponse = z.infer<typeof amortizationResponseSchema>;
export type AmortizationPaginated = z.infer<typeof amortizationPaginatedSchema>;

